<template>
    <div class="flex m-0 border-gray-200 draggable hover:z-20">

        <!-- Draggable handle. -->
        <div class="w-6 py-1.5 m-0 -mt-px border-t border-b bg-white">
            <div class="w-5 h-5 flex items-center pl-1">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move handle"
                   v-if="!estimate.readonly"></i>
            </div>
        </div>

        <div class="flex-1 -ml-px mr-px -mt-px">
            <div class="flex ml-px mr-px">
                <!-- Component -->
                <estimate-task-item-component :estimate="estimate" :item="fixedCost" :ref="'component'" @componentUpdated="updateComponent"></estimate-task-item-component>
                <estimate-task-item-fixed-cost-search
                    class="flex-1 flex"
                    #default="{ open, query, setQuery }"
                    :store="store"
                    :estimate="estimate"
                    @selected:fixedCost="fixedCostSelected"
                    :top="true"
                >
                    <!-- Name. -->
                    <div class="flex-1 flex m-0 -ml-px relative">
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-textarea" v-if="!estimate.readonly">
                <textarea
                    placeholder="Description..."
                    class="text-sm m-0 px-2 py-1.5 border border-gray-200 shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                    type="text"
                    ref="description"
                    v-model="fixedCost.description"
                    rows="1"
                    @input="setQuery($event.target.value);updateDescription()"
                    @change="estimate.buildCollectedItems()"
                    @focus="setQuery($event.target.value);open()"
                ></textarea>
                        </autoresizable-textarea>
                        <div v-else
                             class="text-sm w-full m-0 px-2 py-1.5 border border-gray-200 whitespace-pre-wrap shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                             v-text="fixedCost.description"
                        ></div>
                        <div class="absolute bottom-1 right-1 z-20" v-if="!editAsAssembly">
                            <button class="p-0.5 w-5 h-5 text-xs text-gray-300 hover:text-blue-400"
                                    @click="toggleNotes">
                                <i class="far fa-edit"></i>
                            </button>
                        </div>
                    </div>
                </estimate-task-item-fixed-cost-search>
                <!-- Quantity. -->
                <div class="w-20 -ml-px flex">
                    <page-cell
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Quantity..."
                        :read-only="estimate.readonly"
                        v-model="quantity"
                        ref="quantity"
                        @input="updateQuantity"
                        @change="updateAll"
                    ></page-cell>
                </div>
                <!-- UoM. -->
                <div class="w-24 -ml-px flex">
                    <page-cell
                        currency
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Uom..."
                        :read-only="estimate.readonly"
                        v-model="unit"
                        ref="unit"
                        @input="updateUnit"
                        @change="updateAll"
                    ></page-cell>
                </div>
                <!-- Rate. -->
                <div class="w-24 -ml-px flex">
                    <page-cell
                        currency
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Rate..."
                        :read-only="estimate.readonly"
                        v-model="rate"
                        ref="rate"
                        @input="updateRate"
                        @change="updateAll"
                    ></page-cell>
                </div>

                <!-- Total. -->
                <div class="w-28 -ml-px flex">
                    <page-cell
                        currency
                        class="text-sm m-0 px-2 py-1.5 border border-gray-200 bg-white"
                        placeholder="Total..."
                        :read-only="estimate.readonly"
                        v-model="total"
                        ref="total"
                        @input="updateTotal"
                        @change="updateAll"
                    ></page-cell>
                </div>
            </div>
            <div class="m-0 py-px pb-2" v-if="this.shownotes && !editAsAssembly">
                <div class="m-0 px-px py-px pb+px flex">
                    <div class="w-10 text-gray-700 text-xs pt-0.5 pb-0.5 pl-2 font-medium">Notes:</div>
                    <div class="flex-1">
                        <autoresizable-textarea :height="el => (el.scrollHeight + 1) + 'px'"
                                                ref="notes-textarea" v-if="!estimate.readonly">
                            <textarea
                                placeholder="..."
                                class="col-span-full text-gray-700 text-xs pt-0.5 ml-2  pb-0.5 border-none shadow-none relative hover:z-10 focus:z-20 resize-none focus:ring-0"
                                type="text"
                                ref="notes"
                                v-model="fixedCost.notes"
                                rows="1"
                                @input="estimate.update()"
                                @blur="estimate.update()"
                            ></textarea>
                        </autoresizable-textarea>
                        <div v-else
                             class="w-full whitespace-pre-wrap text-gray-700 text-xs ml-2 pt-0.5 pb-0.5 border-none shadow-none relative hover:z-10 focus:z-20 resize-none focus:ring-0"
                             v-text="fixedCost.notes"
                        ></div>

                    </div>
                </div>
            </div>
        </div>
        <!-- Dropdown. -->
        <div class="w-6 pt-2 m-0 -ml-px -mt-px border-t border-b bg-white">
            <ui-dropdown menu-class="p-3 w-64">
                <template #default="{ toggle }">
                    <button @click="toggle"
                            class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                        <i class="fa fa-ellipsis-h fa-fw"></i>
                    </button>
                </template>
                <template #menu="{ close }">
                    <ui-dropdown-item icon="fas fa-cut" @click="cutFixedCost();close();" v-if="!estimate.readonly">Cut Fixed Cost
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-copy" @click="copyFixedCost();close();">Copy Fixed Cost
                    </ui-dropdown-item>
                    <ui-dropdown-item icon="fas fa-paste" @click="pasteFixedCost();close();"
                                      v-if="fixedCostClipboard.length && !estimate.readonly">
                        {{
                            'Paste Fixed Cost' + (fixedCostClipboard.length > 1 ? 's' : '') + ' (' + fixedCostClipboard.length + ')'
                        }}
                    </ui-dropdown-item>
                    <hr v-if="fixedCostClipboard.length" class="m-1.5"/>
                    <ui-dropdown-item icon="fas fa-clipboard" @click="clearClipboard();close();"
                                      v-if="fixedCostClipboard.length">
                        {{ 'Empty Fixed Cost clipboard' }}
                    </ui-dropdown-item>
                    <hr v-if="!estimate.readonly" class="m-1.5"/>
                    <ui-dropdown-item icon="fas fa-trash" v-if="!estimate.readonly" danger @click="deleteFixedCost();close();">Delete
                    </ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>

    </div>
</template>

<script>
import AutoresizableTextarea from "../../../ui/AutoresizableTextarea";
import {cloneDeep, trim} from "lodash";
import UiDropdownItem from "../../../ui/UiDropdownItem.vue";
import {resolveNumericText} from "../../../../utils";
import remoteStorage from "../../../../utils/remoteStorage";
import EstimateTaskItemComponent from "./EstimateTaskItemComponent.vue";

export default {
    components: {EstimateTaskItemComponent, UiDropdownItem, AutoresizableTextarea},
    props: {
        fixedCost: Object,
        item: Object,
        editAsAssembly: false,
        store: Object,
        estimate: Object,
        fixedCostClipboard: Array,
    },
    data() {
        return {
            shownotes: this.fixedCost.notes !== '',
            unit: this.fixedCost.unit,
            rate: this.fixedCost.rate,
            quantity: this.fixedCost.quantity,
            total: this.fixedCost.total,
            updating: false,
            resolveNumericText,
            color:'#FFFFFF',
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (!this.updating) {
                    this.unit = this.fixedCost.unit;
                    this.rate = this.fixedCost.rate;
                    this.quantity = this.fixedCost.quantity;
                    this.total = this.fixedCost.total;
                }
            },
            deep: true,
        },
    },
    methods: {
        fixedCostSelected({fixedCostDescription, fixedCostUnit, fixedCostRate}) {
            this.fixedCost.description = trim(fixedCostDescription)
            this.fixedCost.unit = trim(fixedCostUnit)
            this.unit = this.fixedCost.unit
            this.rate = parseFloat(('' + fixedCostRate).replace(/[^0-9.]/g, ''))
            this.updateRate()
        },
        toggleNotes() {
            this.shownotes = !this.shownotes
        },
        updateComponent() {
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateDescription() {
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update()
        },
        updateUnit() {
            this.updating = true;
            this.fixedCost.unit = this.unit
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
        },
        updateRate() {
            this.updating = true;
            let value = parseFloat(this.rate === '.' ? '0.' : this.resolveNumericText(this.rate));
            this.fixedCost.rate = isNaN(value) ? 0 : value;
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
            this.total = this.fixedCost.total;
        },
        updateTotal() {
            this.updating = true;
            let value = parseFloat(this.total === '.' ? '0.' : this.resolveNumericText(this.total));
            this.fixedCost.total = isNaN(value) ? 0 : value;
            this.fixedCost.rate = this.fixedCost.total / this.fixedCost.quantity
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
            this.rate = this.fixedCost.rate;
        },
        updateQuantity() {
            this.updating = true;
            let value = parseFloat(this.quantity === '.' ? '0.' : this.resolveNumericText(this.quantity));
            this.fixedCost.quantity = isNaN(value) ? 0 : value;
            this.$emit('assemblyUpdated', this.item)
            this.estimate.update();
            this.total = this.fixedCost.total;
        },
        updateAll() {
            this.rate = parseFloat(this.fixedCost.rate);
            this.quantity = parseFloat(this.fixedCost.quantity);
            this.total = parseFloat(this.fixedCost.total);
            this.estimate.buildCollectedItems()
            this.updating = false;
        },
        deleteFixedCost() {
            this.$emit('assemblyItemRemoved', this.fixedCost, 'fixedCost')
        },
        cutFixedCost() {
            this.fixedCostClipboard.length = 0;
            this.fixedCostClipboard.push(this.fixedCost);
            remoteStorage.setItem('estimate-fixed-cost-clipboard', JSON.stringify(this.fixedCostClipboard))
            this.$emit('assemblyItemRemoved', this.fixedCost, 'fixedCost')
        },
        copyFixedCost() {
            this.fixedCostClipboard.length = 0;
            this.fixedCostClipboard.push(cloneDeep(this.fixedCost))
            remoteStorage.setItem('estimate-fixed-cost-clipboard', JSON.stringify(this.fixedCostClipboard))
        },
        pasteFixedCost() {
            let num = this.item.fixedCosts.indexOf(this.fixedCost);
            this.fixedCostClipboard.forEach((t, index) => {
                let fixedCost = cloneDeep(t);
                this.item.fixedCosts.splice(num + index + 1, 0, fixedCost);
                this.$emit('assemblyItemAdded', fixedCost, 'fixedCost')
            });
            this.estimate.update()
        },
        clearClipboard() {
            this.fixedCostClipboard.length = 0;
            remoteStorage.setItem('estimate-fixed-cost-clipboard', JSON.stringify(this.fixedCostClipboard));
        },
    },
}
</script>
